// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getSettingsCollection } from "@plumberssupply/App/Store/Selectors/SettingsSelectors";
import { color } from "@plumberssupply/XDL/Utilities";
import { truncateOrBreakLineText } from "@plumberssupply/XDL/Utilities";
import { mergeWithCss } from "@plumberssupply/XDL/Utilities/mergeWithCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface TextOptions {
    breakAfter?: number;
    truncateAfter?: number;
}

interface OwnProps {
    categoryId?: string;
    showImage?: boolean;
    extendedStyles?: CategoryItemStyles;
    categoryTitleTextOptions?: TextOptions;
}

type Props = OwnProps;

export interface CategoryItemStyles {
    wrapper?: ClickablePresentationProps;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    categoryTitle?: TypographyPresentationProps;
}

export const categoryItemStyles: CategoryItemStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: flex-start;

            &:focus {
                outline: none;
            }

            &:hover {
                .category-title {
                    color: ${color("secondary")};
                }
            }
        `,
    },
    imageWrapper: {
        css: css`
            width: 100%;
            height: 136px;
            background: ${color("common.white")};
            margin-bottom: 8px;
            justify-content: center;
        `,
    },
    image: {
        css: css`
            width: 100%;
            height: 100%;
        `,
    },
    categoryTitle: {
        color: "common.ui11",
        css:css`
            font-size: 14px;
            font-weight: 800;
            line-height: 18px;
            text-align: center;
            width: 100%;
        `
    },
};

const DEFAULT_TRUNCATE_AFTER = 40;
const DEFAULT_BREAK_AFTER = 24;

const CategoryItem: FC<Props> = ({ categoryId, showImage = true, categoryTitleTextOptions, extendedStyles }) => {
    const {category, notFoundImagePath } = useAppSelector(state => ({
        category: getCategoryState(state, categoryId).value,
        notFoundImagePath: getSettingsCollection(state).customSettings.notFoundMediumImagePath,
    }));

    const [styles] = useState(() => mergeWithCss(categoryItemStyles, extendedStyles));
    if (!category) {
        return null;
    }

    const { breakAfter, truncateAfter } = {
        truncateAfter: DEFAULT_TRUNCATE_AFTER,
        breakAfter: DEFAULT_BREAK_AFTER,
        ...categoryTitleTextOptions,
    };

    return (
        <Clickable {...styles.wrapper} href={category.path} data-test-selector={`category-item_${category.id}`}>
            {showImage && (
                <StyledWrapper {...styles.imageWrapper} className="img-wrapper">
                    <LazyImage
                        src={category.smallImagePath || notFoundImagePath}
                        altText={category.imageAltText || category.shortDescription}
                        {...styles.image}
                    />
                </StyledWrapper>
            )}
            <Typography
                {...styles.categoryTitle}
                className="category-title"
                aria-label={category.shortDescription}
                title={
                    category.shortDescription.length > (breakAfter || DEFAULT_BREAK_AFTER)
                        ? category.shortDescription
                        : ""
                }
            >
                {truncateOrBreakLineText({
                    text: category.shortDescription?.trim(),
                    truncateAfter,
                    breakAfter,
                })}
            </Typography>
        </Clickable>
    );
};

export default CategoryItem;
